/**
 * Turns the row a rejection came back with into the labelled facts the error card shows.
 *
 * The owner's template case (2026-09-23): a shipment on Hold that someone tries to assign to a runner must not
 * just say "مينفعش" — the card has to show the waybill, the status blocking it, the reason behind that status,
 * the runner already holding it, and the notes, so Operations can act without opening another screen.
 *
 * Every field is optional by design: Dispatch fills different columns per endpoint, and the card drops whatever
 * is missing rather than printing empty rows.
 */
import type { ErrorDetail } from './components/ErrorCard'

/** Reads a value under any of the casings/namings Dispatch uses for the same column. */
const pick = (row: Record<string, unknown> | undefined, ...keys: string[]): string | undefined => {
  if (!row) return undefined
  for (const k of keys) {
    const hit = Object.keys(row).find((c) => c.toLowerCase() === k.toLowerCase())
    const v = hit ? row[hit] : undefined
    if (v !== null && v !== undefined && String(v).trim() !== '' && String(v) !== 'False' && String(v) !== '0') return String(v).trim()
  }
  return undefined
}

export interface ShipmentErrorSource {
  awb?: string
  /** The verdict text Dispatch returned for this waybill. */
  message?: string
  statusName?: string
  consignee?: string
  city?: string
  notes?: string
  /** The whole row, for the columns the typed shape does not name. */
  raw?: Record<string, unknown>
}

/**
 * The standard fact grid for anything that failed about one shipment — the same order every time, so an
 * operator learns where to look: what it is, what state blocks it, why, who holds it, what was noted.
 */
export function shipmentErrorDetails(s: ShipmentErrorSource): ErrorDetail[] {
  const r = s.raw
  const status = s.statusName ?? pick(r, 'statusName', 'statusNameA', 'status')
  const runner = pick(r, 'runnerName', 'runnerNameA', 'courierName', 'runner')
  const runnerId = pick(r, 'runnerId', 'runnerID', 'courierId')
  const reason = pick(r, 'reasonName', 'reasonNameA', 'statusReason', 'reason', 'holdReason')

  return [
    { label: 'كود الشحنة', value: s.awb ?? pick(r, 'awb', 'awbNo'), mono: true },
    { label: 'الحالة الحالية', value: status, tone: 'danger' },
    // The verdict Dispatch gave is the reason when it has one; otherwise the status' own reason column.
    { label: 'السبب', value: s.message ?? reason, tone: 'warning' },
    { label: 'المندوب الحالي', value: runner && runnerId ? `${runner} (${runnerId})` : (runner ?? runnerId) },
    { label: 'رقم المندوب', value: pick(r, 'runnerMobile', 'runnerPhone', 'courierMobile') },
    { label: 'المرسل إليه', value: s.consignee ?? pick(r, 'consigneeName', 'consignee') },
    { label: 'المدينة', value: s.city ?? pick(r, 'cityName', 'toCityName') },
    { label: 'ملاحظات الشحنة', value: s.notes ?? pick(r, 'notes', 'specialInstruction', 'specialInstructions') },
    { label: 'ملاحظات العميل', value: pick(r, 'accountNotes', 'clientNotes', 'accountNote') },
    { label: 'العميل', value: pick(r, 'accountName', 'clientName') },
  ]
}

/**
 * The gateway's assign guard answers 422 ASSIGN_BLOCKED with a row per refused shipment
 * (`packages/shared/src/assignRules.ts`). One refusal lays out as the full fact grid; several collapse to the
 * list of waybills, since a toast cannot hold ten grids.
 */
export function blockedAssignDetails(details: unknown): ErrorDetail[] | undefined {
  const blocked = (details as { blocked?: unknown } | undefined)?.blocked
  if (!Array.isArray(blocked) || blocked.length === 0) return undefined
  const rows = blocked as {
    awb?: string; message?: string; statusName?: string; cityName?: string; consignee?: string
    notes?: string; reason?: string; toRunnerName?: string; toRunnerCities?: string[]; postponedTo?: number | null
    heldBy?: { runnerName?: string; transId?: number | null; since?: string } | null
  }[]

  if (rows.length > 1) {
    return [
      { label: 'عدد الشحنات المرفوضة', value: rows.length },
      { label: 'الشحنات', value: rows.map((r) => r.awb).filter(Boolean).join('، '), mono: true },
      { label: 'الأسباب', value: [...new Set(rows.map((r) => r.message).filter(Boolean))].join(' · '), tone: 'warning' },
    ]
  }

  const b = rows[0]!
  return [
    { label: 'كود الشحنة', value: b.awb, mono: true },
    { label: 'الحالة الحالية', value: b.statusName, tone: 'danger' },
    { label: 'السبب', value: b.reason || b.message, tone: 'warning' },
    { label: 'مؤجلة حتى', value: b.postponedTo ? new Date(b.postponedTo).toLocaleDateString('ar-EG') : undefined },
    /*
     * Two different runners, and the labels must say which is which. `heldBy` is whoever has the shipment now;
     * `toRunnerName` is the one it is being handed to. They used to share a field called `runnerName`, which is
     * how the wrong one came to sit under the wrong label (owner, 2026-09-23). When nobody has it, the line says
     * so rather than going blank — "مفيش مندوب يبقى خلاص عادي، قول مفيش".
     */
    {
      label: 'المندوب الحالي',
      value: b.heldBy?.runnerName
        ? [b.heldBy.runnerName, b.heldBy.transId ? `العملية رقم ${b.heldBy.transId}` : '', b.heldBy.since ? `منذ ${b.heldBy.since}` : ''].filter(Boolean).join(' · ')
        : 'لا يوجد مندوب',
      tone: b.heldBy?.runnerName ? 'warning' : 'neutral',
    },
    { label: 'المطلوب إسنادها إلى', value: b.toRunnerName },
    { label: 'مناطق المندوب المطلوب', value: b.toRunnerCities?.join('، ') },
    { label: 'مدينة الشحنة', value: b.cityName },
    { label: 'المرسل إليه', value: b.consignee },
    { label: 'ملاحظات الشحنة', value: b.notes },
  ]
}

/** The same idea for a runner: who it is and why he cannot take the work. */
export function runnerErrorDetails(row: Record<string, unknown> | undefined): ErrorDetail[] {
  return [
    { label: 'المندوب', value: pick(row, 'runnerName', 'runnerNameA', 'name') },
    { label: 'رقم المندوب', value: pick(row, 'runnerId', 'runnerID', 'id'), mono: true },
    { label: 'الفرع', value: pick(row, 'branchName', 'branchesName', 'branch') },
    { label: 'الحالة', value: pick(row, 'statusName', 'status'), tone: 'danger' },
  ]
}
